/**
 * The single place that builds entity URLs.
 *
 * Every segment prefers the human name and falls back to the id, because slugs
 * and nicknames are both optional: rows predating the backfill have no slug,
 * and a user need never choose a nickname. Old /{id}/{id} links therefore keep
 * working forever, and this is what produces them.
 */

import { RECORD_SEPARATOR } from "@/common/slug";
import type { HGCollection, HGRecord } from "@/common/types";

/** How a user appears in a URL. */
export function userRef(user: {
  id: string;
  nickname?: string | null;
}): string {
  return user.nickname || user.id;
}

export function userPath(user: {
  id: string;
  nickname?: string | null;
}): string {
  return `/${userRef(user)}`;
}

export function collectionPath(collection: HGCollection): string {
  const owner = collection.owner_nickname || collection.user_id;
  return `/${owner}/${collection.slug || collection.id}`;
}

/**
 * A record's URL.
 *
 * `ownerRef` lets a caller pass the nickname it already has from the page URL,
 * so list views do not need `owner_nickname` on every record.
 */
export function recordPath(
  record: Pick<HGRecord, "id" | "user_id" | "slug"> & {
    owner_nickname?: string | null;
  },
  ownerRef?: string,
): string {
  const owner = ownerRef || record.owner_nickname || record.user_id;
  // Without a slug there is no pretty form; the legacy permalink is the
  // fallback and is kept routable indefinitely.
  if (!record.slug) return `/record/${record.id}`;
  return `/${owner}/${RECORD_SEPARATOR}/${record.slug}`;
}

/** Absolute URL, for the "this is your link" previews in settings. */
export function absoluteUrl(path: string): string {
  return `${window.location.host}${path}`;
}
